/**
 * Swirl Summary Component
 * Displays AI-generated text summary of Swirl evolution
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader } from 'lucide-react';
import { generateSwirlSummary } from '../../services/aiInsightsService';
import smartSwirlsService from '../../services/smartSwirlsService';

export default function SwirlSummary({ swirlId, evolutionData }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadSummary = async () => {
      if (!swirlId) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch evolution data if not provided
        let evolution = evolutionData;
        if (!evolution) {
          const { data } = await smartSwirlsService.getSwirlEvolution(swirlId);
          evolution = data || [];
        }

        const summaryText = await generateSwirlSummary(swirlId, evolution);
        setSummary(summaryText);
      } catch (err) {
        console.error('Error loading Swirl summary:', err);
        setError('Failed to generate summary');
        setSummary("Your curiosity in this space has evolved beautifully — themes and connections are deepening over time.");
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, [swirlId, evolutionData]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white/70 backdrop-blur-sm rounded-xl border border-neutral-200 p-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-purple-600" />
        <h3 className="text-lg font-semibold text-neutral-800" style={{ 
          fontFamily: 'Inter, sans-serif',
          fontSize: '16px',
          opacity: 0.9
        }}>
          Evolution Summary
        </h3>
      </div>

      <p className="text-xs text-neutral-500 mb-4 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
        A visual reflection of your evolving focus.
      </p>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 text-neutral-500"
          >
            <Loader className="w-4 h-4 animate-spin" />
            <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              Generating insights...
            </span>
          </motion.div>
        ) : summary ? (
          <motion.p
            key="summary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-neutral-700 leading-relaxed"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '15px',
              opacity: 0.9
            }}
          >
            {summary}
          </motion.p>
        ) : error ? (
          <motion.p
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-red-600 text-sm"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {error}
          </motion.p>
        ) : null}
      </AnimatePresence>
    </motion.div>
  );
}

